// OVO-149/150 — single matcher for the day-plan's stop filter (the input
// next to the date picker), the SearchStops popup, the map-marker filter,
// and the "only matching unscheduled orders get optimized" set. One place
// so the four surfaces can never drift apart again.
//
// Notes:
// - Plain substring matching (`includes`), NOT `String.match` — the old
//   matchers treated the user's input as a REGEX, so a stray "(" or "+"
//   in the query threw a SyntaxError and broke the filter entirely.
// - `orderRef` / `partnerOrderRef` are included because that's the id the
//   operator usually copies from the orders table — searching it used to
//   silently find nothing (OVO-150's "sometimes doesn't work").
// - `partnerName` / `partnerCode` come from `order.partnerRef` (the real
//   relation); the bare `partner` field is the legacy free-text column,
//   kept for back-compat.
// - Zip codes match through `address` (they're part of the string).
// OVO-205 — `name` (the pickup/delivery name → for deliveries the recipient's
// personal name) was REMOVED from the DEFAULT match set: it caused false
// positives when FILTERING the plan (typing partner "Epi" matched a recipient
// named "Epita"). The day-plan filter is for partner + location identity —
// partner is covered by partnerName / partnerCode, the site street by `address`.
const MATCH_FIELDS = [
  'address',
  'driverName',
  'orderCode',
  'orderRef',
  'partnerOrderRef',
  'partner',
  'partnerName',
  'partnerCode',
  'volume',
  // OVO-250 — order `types[]` (tour/skill tags, e.g. "BUT-Jeudi-Bike-GE-1").
  // Array field: each entry is matched individually (see the flatMap below),
  // so "BUT" matches every BUT-* tournée and the full tag matches exactly one.
  // Since the filter also scopes Optimize, this gives "optimize one tournée".
  'orderTypes',
];

// OVO-228 — the recipient / pickup NAME. Opt-in via { includeName: true } —
// used ONLY by the SearchStops popup, where the operator looks up ONE specific
// stop by the name they see on it (e.g. "Anna Giavazzi", reported Jun 30 2026 as
// un-findable in the map search even though the stop exists on Route #1846).
// The inline day-plan FILTER + map-marker filter + optimize-set keep it OUT, so
// OVO-205's "Epi ≠ Epita" precision is preserved when narrowing the plan. `name`
// covers both sides (pickupName for pickups, deliveryName for deliveries).
const NAME_FIELDS = ['name', 'deliveryName'];

export function stopMatchesQuery(stop, rawQuery, { includeName = false } = {}) {
  if (!rawQuery) return true;
  if (!stop) return false;
  // OVO-205 — split into space/comma-separated terms combined with AND, so
  // "épi 1202" matches EPI orders at zip 1202. Each term stays a plain
  // substring match (never a regex — see the note above).
  const terms = String(rawQuery)
    .toLowerCase()
    .split(/[\s,]+/)
    .filter(Boolean);
  if (terms.length === 0) return true;
  const fields = includeName ? [...MATCH_FIELDS, ...NAME_FIELDS] : MATCH_FIELDS;
  const haystack = fields
    // Array-valued fields (orderTypes) contribute one haystack entry per
    // element — never a joined string, so a term can't match across two tags.
    .flatMap((field) => {
      const v = stop[field];
      if (v == null) return [];
      return Array.isArray(v) ? v : [v];
    })
    .filter((v) => v != null)
    .map((v) => String(v).toLowerCase());
  return terms.every((term) => haystack.some((v) => v.includes(term)));
}

// ---------------------------------------------------------------------------
// Advanced filter (Sep 2026) — the "Advanced" toggle next to the day-plan
// filter input lets the operator EXPLICITLY pick one or more partners and/or
// one or more zip codes, instead of typing text that may match by accident
// (the OVO-205 "Epi ≠ Epita" class). The three criteria are AND-combined:
//   text query  → stopMatchesQuery (unchanged)
//   partnerIds  → stop.partnerId ∈ set (the real Partner FK, never free text)
//   zips        → the stop's OWN address zip ∈ set (see extractZip)
// One matcher for the map pins, the unscheduled list and the optimize set —
// same rule as stopMatchesQuery: the surfaces must never drift apart.
// ---------------------------------------------------------------------------

const COUNTRY_TOKENS = new Set([
  'ch',
  'che',
  'suisse',
  'switzerland',
  'schweiz',
  'svizzera',
  'fr',
  'france',
  'de',
  'germany',
  'deutschland',
  'it',
  'italia',
  'italy',
]);

// A locality candidate is usable when it's a real word (not a country, not a
// 2-letter canton code like "GE"/"VD", not empty / numeric).
const usableLocality = (s) => {
  const v = String(s || '')
    .trim()
    .replace(/^[\s,.-]+|[\s,.-]+$/g, '');
  if (!v) return null;
  if (/\d/.test(v)) return null;
  if (/^[A-Z]{2}$/.test(v)) return null;
  if (COUNTRY_TOKENS.has(v.toLowerCase())) return null;
  return v;
};

const zipCache = new Map();

// Extracts `{ zip, locality }` from a free-text address, or null.
//
// Swiss zips are 4 digits, French 5 — both accepted. Street numbers can be
// 4 digits too ("Route de Chêne 1000, 1224 Chêne-Bougeries"), so a token that
// STARTS with the zip (or is "GE 1202") wins over any other 4-digit run; only
// when no such token exists does the LAST bare 4–5 digit run count (the zip
// normally follows the street). The locality is the text after the zip in the
// same comma-token ("1227 Geneve"), else the next token ("1208, Genève, CH"),
// else the previous one ("Vernier, GE 1219, Switzerland") — skipping countries
// and canton codes. Cached per address string (called for every pin on every
// filter change).
export function extractZip(address) {
  if (!address) return null;
  const key = String(address);
  if (zipCache.has(key)) return zipCache.get(key);
  if (zipCache.size > 5000) zipCache.clear();

  const tokens = key
    .replace(/[\r\n]+/g, ',')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  let hit = null;
  // 1) a token that begins with the zip (optionally after a canton code)
  for (let i = 0; i < tokens.length; i++) {
    const m = tokens[i].match(/^(?:[A-Z]{2}\s+)?(\d{4,5})\b\s*(.*)$/);
    if (m) {
      hit = { idx: i, zip: m[1], after: m[2], before: '' };
      break;
    }
  }
  // 2) else the last token containing a bare 4–5 digit run
  if (!hit) {
    for (let i = tokens.length - 1; i >= 0; i--) {
      const m = tokens[i].match(/^(.*?)\b(\d{4,5})\b\s*(.*)$/);
      if (m) {
        hit = { idx: i, zip: m[2], after: m[3], before: m[1] };
        break;
      }
    }
  }
  let result = null;
  if (hit) {
    const locality =
      usableLocality(hit.after) ||
      usableLocality(tokens[hit.idx + 1]) ||
      usableLocality(hit.before) ||
      usableLocality(tokens[hit.idx - 1]) ||
      null;
    result = { zip: hit.zip, locality };
  }
  zipCache.set(key, result);
  return result;
}

// #47 — `{ zip, locality }` for exports: extractZip, else (no postal code in
// the address, e.g. "Av. de France 17, Genève, Suisse") the last usable
// comma-token that isn't the first one (the street) → `{ zip: null, locality }`.
export function addressZipLocality(address) {
  const hit = extractZip(address);
  if (hit) return hit;
  if (!address) return null;
  const tokens = String(address)
    .replace(/[\r\n]+/g, ',')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  for (let i = tokens.length - 1; i > 0; i--) {
    const loc = usableLocality(tokens[i]);
    if (loc) return { zip: null, locality: loc };
  }
  return null;
}

export const EMPTY_STOP_FILTER = Object.freeze({ query: '', partnerIds: [], zips: [] });

export function isStopFilterEmpty(filter) {
  if (!filter) return true;
  return !filter.query && !(filter.partnerIds && filter.partnerIds.length) && !(filter.zips && filter.zips.length);
}

export function stopMatchesFilter(stop, filter, opts) {
  if (isStopFilterEmpty(filter)) return true;
  if (!stop) return false;
  if (filter.query && !stopMatchesQuery(stop, filter.query, opts)) return false;
  if (filter.partnerIds && filter.partnerIds.length) {
    const pid = stop.partnerId;
    if (pid == null) return false;
    if (!filter.partnerIds.some((id) => String(id) === String(pid))) return false;
  }
  if (filter.zips && filter.zips.length) {
    const z = extractZip(stop.address)?.zip;
    if (!z) return false;
    if (!filter.zips.some((zip) => String(zip) === z)) return false;
  }
  return true;
}
